import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import BulkActionBar from '@/components/common/BulkActionBar'
import ExportDialog from '@/components/common/ExportDialog'
import ImportDialog from '@/components/common/ImportDialog'
import CreateAgentModal from '@/components/agents/CreateAgentModal'
import CreateCentralizedAgentWizard from '@/components/agents/CreateCentralizedAgentWizard'
import { TagFilter, TagBadges } from '@/components/common/TagFilter'
import { agentStore } from '@/stores/AgentStore'
import { useBulkSelection } from '@/hooks/useBulkSelection'
import { exportService } from '@/services/exportService'
import { importService } from '@/services/importService'
import type { ExportOptions } from '@/components/common/ExportDialog'
import type { ImportOptions } from '@/components/common/ImportDialog'
import type { ImportResult } from '@/services/importService'
import {
  CpuChipIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PlayIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  ArrowUpTrayIcon,
  SparklesIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import type { Agent, AgentFramework, AgentStatus, AgentCreate, CentralizedAgent, AgentTag } from '@/types'
import { ALL_AGENT_TAGS } from '@/types'
import toast from 'react-hot-toast'
import AgentDetailModal from '@/components/agents/AgentDetailModal'

const AgentsPage = observer(() => {
  const navigate = useNavigate()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showWizard, setShowWizard] = useState(false)
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<any>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [filterTier, setFilterTier] = useState<string>('all')
  const [filterVariant, setFilterVariant] = useState<string>('all')
  const bulkSelection = useBulkSelection<Agent>(agentStore.filteredAgents)

  useEffect(() => {
    // Reset filters and pagination when page mounts to ensure clean state
    agentStore.clearFilters()
    agentStore.setPage(1)
    agentStore.fetchAgents()
    agentStore.fetchCentralizedAgents()
  }, [])

  const getFrameworkBadgeVariant = (framework: AgentFramework) => {
    const variants: Record<AgentFramework, 'primary' | 'success' | 'warning' | 'error'> = {
      langchain: 'primary',
      crewai: 'success',
      adk: 'warning',
      custom: 'error',
    }
    return variants[framework]
  }

  const getStatusBadgeVariant = (status: AgentStatus) => {
    const variants: Record<AgentStatus, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
      draft: 'gray',
      active: 'success',
      inactive: 'gray',
      deploying: 'warning',
      error: 'error',
      archived: 'gray',
    }
    return variants[status]
  }

  const handleDeleteAgent = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await agentStore.deleteAgent(id)
      } catch (error) {
        console.error('Failed to delete agent:', error)
      }
    }
  }

  // Execute opens Playground with the agent preselected — Playground is the
  // unified run surface now, with per-agent output renderers for results.
  // See PLAN-argus-demo-and-agent-output-renderers.md §3.4.
  const handleExecuteAgent = (agent: Agent) => {
    navigate(`/playground?agent=${encodeURIComponent(agent.name || agent.id)}`)
  }

  const handleExecuteCentralizedAgent = (agent: CentralizedAgent) => {
    navigate(`/playground?agent=${encodeURIComponent(agent.name)}`)
  }

  const handleBulkDelete = async () => {
    const count = bulkSelection.selectedCount
    if (!window.confirm(`Are you sure you want to delete ${count} agent${count > 1 ? 's' : ''}?`)) {
      return
    }

    try {
      const promises = bulkSelection.selectedItems.map((agent) =>
        agentStore.deleteAgent(agent.id)
      )
      await Promise.all(promises)
      toast.success(`Successfully deleted ${count} agent${count > 1 ? 's' : ''}`)
      bulkSelection.deselectAll()
    } catch (error) {
      toast.error('Failed to delete some agents')
    }
  }

  const handleBulkArchive = async () => {
    const count = bulkSelection.selectedCount
    try {
      const promises = bulkSelection.selectedItems.map((agent) =>
        agentStore.updateAgent(agent.id, { status: 'archived' as AgentStatus })
      )
      await Promise.all(promises)
      toast.success(`Successfully archived ${count} agent${count > 1 ? 's' : ''}`)
      bulkSelection.deselectAll()
    } catch (error) {
      toast.error('Failed to archive some agents')
    }
  }

  const handleBulkActivate = async () => {
    const count = bulkSelection.selectedCount
    try {
      const promises = bulkSelection.selectedItems.map((agent) =>
        agentStore.updateAgent(agent.id, { status: 'active' as AgentStatus })
      )
      await Promise.all(promises)
      toast.success(`Successfully activated ${count} agent${count > 1 ? 's' : ''}`)
      bulkSelection.deselectAll()
    } catch (error) {
      toast.error('Failed to activate some agents')
    }
  }

  const handleBulkExport = () => {
    setShowExportDialog(true)
  }

  const handleExport = (options: ExportOptions) => {
    try {
      const data = exportService.exportAgents(bulkSelection.selectedItems, options)
      const filename = exportService.generateFilename('agent', bulkSelection.selectedCount)
      exportService.downloadJSON(data, filename)
      toast.success(`Exported ${bulkSelection.selectedCount} agent${bulkSelection.selectedCount > 1 ? 's' : ''}`)
      bulkSelection.deselectAll()
    } catch (error) {
      toast.error('Failed to export agents')
    }
  }

  const handleImport = async (file: File, options: ImportOptions): Promise<ImportResult<AgentCreate>> => {
    try {
      const jsonData = await importService.parseJSONFile(file)
      return await importService.importAgents(jsonData, options)
    } catch (error: any) {
      throw new Error(error.message || 'Failed to parse file')
    }
  }

  const handleImportConfirm = async (agents: AgentCreate[]) => {
    try {
      const promises = agents.map(agent => agentStore.createAgent(agent))
      await Promise.all(promises)
      await agentStore.fetchAgents()
      toast.success(`Imported ${agents.length} agent${agents.length > 1 ? 's' : ''}`)
    } catch (error) {
      toast.error('Failed to import some agents')
      throw error
    }
  }

  // Render centralized agent card
  const renderCentralizedAgentCard = (agent: CentralizedAgent) => (
    <Card key={agent.name} className="hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-purple-500/100/15 to-blue-500/100/15 rounded-lg">
              <SparklesIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {agent.display_name}
              </h3>
              <span className="text-xs text-muted-foreground">{agent.name}</span>
            </div>
            {agent.enabled ? (
              <Badge variant="success">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Enabled
              </Badge>
            ) : (
              <Badge variant="gray">
                <XCircleIcon className="w-3 h-3 mr-1" />
                Disabled
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {agent.description || 'No description'}
          </p>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Domain:</span>
          <Badge variant="primary">{agent.domain}</Badge>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Tier:</span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            (agent as any).tier === 3 ? 'bg-purple-500/20 text-purple-600' :
            (agent as any).tier === 2 ? 'bg-blue-500/20 text-blue-600' :
            'bg-muted text-muted-foreground'
          }`}>
            {(agent as any).tier === 3 ? 'Tier 3 — Multi-Action' :
             (agent as any).tier === 2 ? 'Tier 2 — Domain-Aware' :
             'Tier 1 — Specialist'}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Type:</span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            ((agent as any).agent_type || 'internal') === 'external'
              ? 'bg-orange-500/20 text-orange-600'
              : 'bg-blue-500/20 text-blue-600'
          }`}>
            {((agent as any).agent_type || 'internal') === 'external' ? 'External' : 'Internal'}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Variant:</span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            ((agent as any).variant || 'specialized') === 'standardized'
              ? 'bg-emerald-500/20 text-emerald-600'
              : 'bg-muted text-foreground'
          }`}>
            {((agent as any).variant || 'specialized') === 'standardized' ? 'Standardized' : 'Specialized'}
          </span>
        </div>

        {((agent as any).supported_modalities || ['text']).length > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Modalities:</span>
            <div className="flex gap-1">
              {((agent as any).supported_modalities || ['text']).map((mod: string) => (
                <span key={mod} className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-muted text-foreground capitalize">
                  {mod}
                </span>
              ))}
            </div>
          </div>
        )}

        {agent.version && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Version:</span>
            <span className="font-medium text-foreground">v{agent.version}</span>
          </div>
        )}

        {agent.tags && agent.tags.length > 0 && (
          <div className="pt-2">
            <span className="text-xs text-muted-foreground block mb-1.5">Tags:</span>
            <TagBadges tags={agent.tags} maxVisible={4} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-4 border-t border-border">
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleExecuteCentralizedAgent(agent)}
          disabled={!agent.enabled}
          className="flex-1 sm:flex-none"
        >
          <PlayIcon className="w-4 h-4 mr-1" />
          Execute
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => { setSelectedAgent(agent); setShowDetailModal(true) }}
          className="flex-1 sm:flex-none"
        >
          View Details
        </Button>
      </div>
    </Card>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Agents</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Build and manage AI agents
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowImportDialog(true)}
            size="sm"
            className="flex-1 sm:flex-none"
          >
            <ArrowUpTrayIcon className="w-5 h-5 sm:mr-2" />
            <span className="hidden sm:inline">Import</span>
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowWizard(true)}
            size="sm"
            className="flex-1 sm:flex-none"
            title="Create a new centralized Ziro agent (generates code, patches metadata, requires restart)"
          >
            <PlusIcon className="w-5 h-5 sm:mr-2" />
            <span className="hidden sm:inline">Create Ziro Agent</span>
            <span className="sm:hidden">Ziro</span>
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            size="sm"
            className="flex-1 sm:flex-none"
            title="Create a custom user agent (stored in DB, uses your own prompts + tools)"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            <span className="hidden xs:inline">Create Agent</span>
            <span className="xs:hidden">Create</span>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => agentStore.setActiveTab('system')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              agentStore.activeTab === 'system'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            <SparklesIcon className="w-5 h-5 inline-block mr-2 -mt-0.5" />
            Internal Agents
            <span className="ml-2 bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs">
              {agentStore.centralizedAgents.length}
            </span>
          </button>
          <button
            onClick={() => agentStore.setActiveTab('custom')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              agentStore.activeTab === 'custom'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            <CpuChipIcon className="w-5 h-5 inline-block mr-2 -mt-0.5" />
            External Agents
            <span className="ml-2 bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs">
              {agentStore.totalAgents}
            </span>
          </button>
        </nav>
      </div>

      {/* Internal Agents Tab */}
      {agentStore.activeTab === 'system' && (
        <>
          {/* Tag Filters */}
          <Card>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search system agents..."
                      value={agentStore.searchQuery}
                      onChange={(e) => agentStore.setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
                    />
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => agentStore.clearFilters()}
                  disabled={!agentStore.hasActiveCentralizedFilters}
                  className="whitespace-nowrap"
                >
                  <FunnelIcon className="w-5 h-5 sm:mr-2" />
                  <span className="hidden sm:inline">Clear Filters</span>
                </Button>
              </div>

              {/* Tier + Variant on one row (Type filter removed — already split via tabs above) */}
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <div>
                  <span className="text-xs text-muted-foreground block mb-2">Tier:</span>
                  <div className="flex gap-1.5">
                    {[
                      { value: 'all', label: 'All' },
                      { value: '1', label: 'T1' },
                      { value: '2', label: 'T2' },
                      { value: '3', label: 'T3' },
                    ].map((opt) => {
                      const selected = filterTier === opt.value
                      const isAll = opt.value === 'all'
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setFilterTier(opt.value)}
                          className="px-2.5 py-1 text-xs rounded-full border transition-all font-medium"
                          style={
                            selected
                              ? isAll
                                ? { backgroundColor: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))', borderColor: 'hsl(var(--primary))' }
                                : { backgroundColor: '#8b5cf615', color: '#8b5cf6', borderColor: '#8b5cf630' }
                              : { backgroundColor: 'transparent', color: 'hsl(var(--muted-foreground))', borderColor: 'hsl(var(--border))' }
                          }
                        >
                          {opt.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block mb-2">Variant:</span>
                  <div className="flex gap-1.5">
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'specialized', label: 'Specialized' },
                      { value: 'standardized', label: 'Standardized' },
                    ].map((opt) => {
                      const selected = filterVariant === opt.value
                      const isAll = opt.value === 'all'
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setFilterVariant(opt.value)}
                          className="px-2.5 py-1 text-xs rounded-full border transition-all font-medium"
                          style={
                            selected
                              ? isAll
                                ? { backgroundColor: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))', borderColor: 'hsl(var(--primary))' }
                                : { backgroundColor: '#10b98115', color: '#10b981', borderColor: '#10b98130' }
                              : { backgroundColor: 'transparent', color: 'hsl(var(--muted-foreground))', borderColor: 'hsl(var(--border))' }
                          }
                        >
                          {opt.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* Category pills on their own row below Tier/Variant */}
              <div>
                <span className="text-xs text-muted-foreground block mb-2">Category:</span>
                <TagFilter<AgentTag>
                  tags={ALL_AGENT_TAGS}
                  selectedTags={agentStore.selectedTags}
                  onTagToggle={(tag) => agentStore.toggleTag(tag)}
                  onClear={() => agentStore.clearSelectedTags()}
                />
              </div>
            </div>
          </Card>

          {/* Centralized Agents List */}
          {agentStore.centralizedLoading ? (
            <Card className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-muted-foreground mt-4">Loading system agents...</p>
            </Card>
          ) : agentStore.filteredCentralizedAgents.length === 0 ? (
            <Card className="text-center py-12">
              <SparklesIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {agentStore.hasActiveCentralizedFilters ? 'No agents match your filters' : 'No system agents available'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {agentStore.hasActiveCentralizedFilters
                  ? 'Try adjusting your search or tag filters'
                  : 'System agents are registered automatically by the backend'}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {agentStore.filteredCentralizedAgents
                .filter((a: any) => filterTier === 'all' || String((a as any).tier ?? 1) === filterTier)
                .filter((a: any) => filterVariant === 'all' || ((a as any).variant || 'specialized') === filterVariant)
                .filter((a: any, idx: number, arr: any[]) => arr.findIndex((x: any) => x.name === a.name) === idx)
                .map(renderCentralizedAgentCard)}
            </div>
          )}

          {agentStore.centralizedError && (
            <Card className="bg-red-500/10 border-red-500/20">
              <div className="flex items-center justify-between">
                <p className="text-red-600">{agentStore.centralizedError}</p>
                <Button variant="ghost" size="sm" onClick={() => agentStore.fetchCentralizedAgents()}>
                  Retry
                </Button>
              </div>
            </Card>
          )}
        </>
      )}

      {/* External Agents Tab */}
      {agentStore.activeTab === 'custom' && (
        <>
          {/* Filters and Search */}
          <Card>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search agents..."
                      value={agentStore.searchQuery}
                      onChange={(e) => agentStore.setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
                    />
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => agentStore.clearFilters()}
                  disabled={!agentStore.hasActiveFilters}
                  className="whitespace-nowrap"
                >
                  <FunnelIcon className="w-5 h-5 sm:mr-2" />
                  <span className="hidden sm:inline">Clear Filters</span>
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <select
                  value={agentStore.frameworkFilter}
                  onChange={(e) =>
                    agentStore.setFrameworkFilter(e.target.value as AgentFramework | 'all')
                  }
                  className="flex-1 px-3 sm:px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
                >
                  <option value="all">All Frameworks</option>
                  <option value="langchain">LangChain</option>
                  <option value="crewai">CrewAI</option>
                  <option value="adk">ADK</option>
                  <option value="custom">Custom</option>
                </select>

                <select
                  value={agentStore.statusFilter}
                  onChange={(e) =>
                    agentStore.setStatusFilter(e.target.value as AgentStatus | 'all')
                  }
                  className="flex-1 px-3 sm:px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="error">Error</option>
                </select>
              </div>
            </div>
          </Card>

          {/* Agent List */}
          {agentStore.isLoading ? (
            <Card className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-muted-foreground mt-4">Loading agents...</p>
            </Card>
          ) : agentStore.filteredAgents.length === 0 ? (
            <Card className="text-center py-12">
              <CpuChipIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {agentStore.hasActiveFilters ? 'No agents match your filters' : 'No agents yet'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {agentStore.hasActiveFilters
                  ? 'Try adjusting your search or filters'
                  : 'Create your first AI agent to get started'}
              </p>
              {!agentStore.hasActiveFilters && (
                <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Create Agent
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {agentStore.filteredAgents.map((agent) => (
                <Card key={agent.id} className="hover:shadow-lg transition-shadow relative" data-testid="agent-card">
                  {/* Bulk Selection Checkbox */}
                  <div className="absolute top-4 left-4">
                    <input
                      type="checkbox"
                      checked={bulkSelection.isSelected(agent.id)}
                      onChange={() => bulkSelection.toggleSelection(agent.id)}
                      className="w-5 h-5 text-primary-600 border-border rounded focus:ring-primary-500 cursor-pointer"
                    />
                  </div>

                  <div className="flex items-start justify-between mb-4 ml-8">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">
                          {agent.name}
                        </h3>
                        <Badge variant={getStatusBadgeVariant(agent.status)}>
                          {agent.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {agent.description || 'No description'}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Framework:</span>
                      <Badge variant={getFrameworkBadgeVariant(agent.framework)}>
                        {agent.framework.toUpperCase()}
                      </Badge>
                    </div>

                    {agent.model && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Model:</span>
                        <span className="font-medium text-foreground">{agent.model}</span>
                      </div>
                    )}

                    {agent.tools && agent.tools.length > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Tools:</span>
                        <span className="font-medium text-foreground">
                          {agent.tools.length} tool{agent.tools.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Created:</span>
                      <span className="text-foreground">
                        {formatDistanceToNow(new Date(agent.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-border">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleExecuteAgent(agent)}
                      disabled={agent.status !== 'active'}
                      className="flex-1 sm:flex-none"
                    >
                      <PlayIcon className="w-4 h-4 mr-1" />
                      Execute
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/agents/${agent.id}`)}
                      className="flex-1 sm:flex-none"
                    >
                      <PencilIcon className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteAgent(agent.id, agent.name)}
                      className="flex-1 sm:flex-none"
                    >
                      <TrashIcon className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination */}
          {agentStore.totalAgents > agentStore.pageSize && (
            <Card>
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {(agentStore.currentPage - 1) * agentStore.pageSize + 1} to{' '}
                  {Math.min(
                    agentStore.currentPage * agentStore.pageSize,
                    agentStore.totalAgents
                  )}{' '}
                  of {agentStore.totalAgents} agents
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => agentStore.setPage(agentStore.currentPage - 1)}
                    disabled={agentStore.currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-foreground">
                    Page {agentStore.currentPage} of{' '}
                    {Math.ceil(agentStore.totalAgents / agentStore.pageSize)}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => agentStore.setPage(agentStore.currentPage + 1)}
                    disabled={
                      agentStore.currentPage >=
                      Math.ceil(agentStore.totalAgents / agentStore.pageSize)
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Error Display */}
      {agentStore.error && (
        <Card className="bg-red-500/10 border-red-500/20">
          <div className="flex items-center justify-between">
            <p className="text-red-600">{agentStore.error}</p>
            <Button variant="ghost" size="sm" onClick={() => agentStore.clearError()}>
              Dismiss
            </Button>
          </div>
        </Card>
      )}

      {/* Create Agent Modal (custom external agents) */}
      <CreateAgentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Create Centralized Ziro Agent Wizard (Phase 3b) */}
      <CreateCentralizedAgentWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onCreated={() => {
          // Wizard will close on its own after showing success; refetch centralized list
          agentStore.fetchCentralizedAgents()
        }}
      />

      {/* Export Dialog */}
      <ExportDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        title="Export Agents"
        itemCount={bulkSelection.selectedCount}
        itemType="agent"
        onExport={handleExport}
      />

      {/* Import Dialog */}
      <ImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        title="Import Agents"
        itemType="agent"
        onImport={handleImport}
        onConfirm={handleImportConfirm}
      />

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={bulkSelection.selectedCount}
        totalCount={agentStore.filteredAgents.length}
        onSelectAll={() => bulkSelection.selectAll(agentStore.filteredAgents)}
        onDeselectAll={bulkSelection.deselectAll}
        onDelete={handleBulkDelete}
        onArchive={handleBulkArchive}
        onActivate={handleBulkActivate}
        onExport={handleBulkExport}
      />
      {/* Agent Detail Modal */}
      <AgentDetailModal
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedAgent(null) }}
        agent={selectedAgent}
        isCentralized={agentStore.activeTab === 'system'}
      />
    </div>
  )
})

export default AgentsPage
